/**
 * Application constants and configuration values
 * 
 * AI Agent Note: This file contains all constant values used throughout the app.
 * Update device types, USB types, or labels here to affect the entire application.
 */

import { DeviceType, USBVersion } from '../types';

/**
 * Device type labels for UI display
 */
export const DEVICE_TYPE_LABELS: Record<DeviceType, string> = {
  'angle-adjuster': 'Angle Adjuster',
  'camera': 'Camera',
  'computer': 'Computer / ASIAIR',
  'dew-heater': 'Dew Heater',
  'filter-wheel': 'Filter Wheel',
  'flat-panel': 'Flat Panel',
  'focuser': 'Focuser',
  'guide-camera': 'Guide Camera',
  'mount': 'Mount',
  'other': 'Other',
  'power-box': 'Power Box',
  'power-source': 'Power Source',
  'usb-hub': 'USB Hub'
};

/**
 * Device type icons - image paths for visual representation
 * Images located in public/images/ folder
 * Note: 'other' type uses emoji as fallback since no specific image exists
 */
export const DEVICE_TYPE_ICONS: Record<DeviceType, string> = {
  'angle-adjuster': '/images/angle-adjuster.png',
  'camera': '/images/camera.png',
  'computer': '/images/computer.png',
  'dew-heater': '/images/dew-heater.png',
  'filter-wheel': '/images/filter-wheel.png',
  'flat-panel': '/images/flat-panel.png',
  'focuser': '/images/focuser.png',
  'guide-camera': '/images/guide-camera.png',
  'mount': '/images/mount.png',
  'other': '⚙️',  // Emoji fallback for 'other' type
  'power-box': '/images/power-box.png',
  'power-source': '/images/power-source.png',
  'usb-hub': '/images/usb-hub.png'
};

/**
 * Available USB port types
 */
export const USB_VERSION_OPTIONS: USBVersion[] = [
  'USB 2.0',
  'USB 3.0',
  'USB 3.1',
  'USB-C'
];

/**
 * Power calculation constants
 */
export const POWER_CONSTANTS = {
  /**
   * Minimum power requirement in Watts
   */
  MIN_POWER: 0,
  
  /**
   * Default power step for input fields
   */
  POWER_STEP: 0.1,
  
  /**
   * Decimal places for power display
   */
  POWER_DECIMALS: 2,
} as const;

/**
 * Validation messages
 */
export const VALIDATION_MESSAGES = {
  NO_POWER_SOURCE: (deviceName: string) => 
    `${deviceName} is not connected to any power source`,
  
  POWER_CHAIN_EXCEEDED: (deviceName: string, passthrough: number, actual: number) => 
    `${deviceName} passthrough power (${passthrough}W) exceeded by connected devices (${actual.toFixed(POWER_CONSTANTS.POWER_DECIMALS)}W)`,
  
  POWER_EXCEEDED: (deviceName: string, capacity: number, exceeded: number) => 
    `Power source ${deviceName} capacity (${capacity}W) exceeded by ${exceeded.toFixed(POWER_CONSTANTS.POWER_DECIMALS)}W`,
  
  CIRCULAR_DEPENDENCY: 'This connection would create a circular dependency',
} as const;
